import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import SEO from "../components/SEO";

/**
 * Unsubscribe landing page — reads ?email=…&token=… from the URL, calls the
 * backend to remove the subscriber, and shows a warm little confirmation.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const [state, setState] = useState("working"); // working | done | error
  const [error, setError] = useState("");

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    if (!email || !token) {
      setState("error");
      setError("This link is missing an email or token.");
      return;
    }
    axios
      .get(`${backend}/api/unsubscribe`, { params: { email, token } })
      .then(() => setState("done"))
      .catch((err) => {
        setState("error");
        setError(err?.response?.data?.detail || "Something went wrong.");
      });
  }, [email, token]);

  return (
    <div
      className="parchment min-h-screen pt-24 pb-24 px-6 flex items-center justify-center"
      data-testid="unsubscribe-page"
    >
      <SEO
        title="Unsubscribed · The Salty Moo™"
        description="You've been taken off the list. The gate is always open, friend."
        path="/unsubscribe"
      />
      <div className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md max-w-xl w-full text-center px-10 py-14">
        <p className="text-xs uppercase tracking-[0.4em] text-[#D62839] mb-4">
          Front Porch
        </p>

        {state === "working" && (
          <>
            <h1 className="font-display text-4xl md:text-5xl text-[#4B3A2F] leading-tight mb-4">
              One moment, friend&hellip;
            </h1>
            <p data-testid="unsub-status-working" className="font-body text-lg text-[#2C2724]/85">
              Taking you off the list gently.
            </p>
          </>
        )}

        {state === "done" && (
          <>
            <h1 className="font-display text-5xl md:text-6xl text-[#4B3A2F] leading-tight mb-4">
              You&rsquo;re off the list.
            </h1>
            <div className="gold-rule mx-auto w-24 mb-6" />
            <p className="font-script text-3xl text-[#4B3A2F] mb-3">
              no hard feelings.
            </p>
            <p
              data-testid="unsub-status-done"
              className="font-body text-lg text-[#2C2724]/85 leading-relaxed mb-8"
            >
              {email ? (
                <><span className="font-body text-[#4B3A2F]">{email}</span>{" "}</>
              ) : null}
              won&rsquo;t hear from the pasture again. If this was a
              wander&mdash;off in the wrong direction, the gate is always
              open. You can rejoin the porch any time.
            </p>
            <Link
              to="/front-porch"
              data-testid="unsub-rejoin-btn"
              className="btn-pasture inline-flex items-center justify-center"
            >
              Come back to the porch
            </Link>
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="font-display text-4xl md:text-5xl text-[#4B3A2F] leading-tight mb-4">
              Hmm, that link didn&rsquo;t work.
            </h1>
            <div className="gold-rule mx-auto w-24 mb-6" />
            <p
              data-testid="unsub-status-error"
              className="font-body text-lg text-[#2C2724]/85 leading-relaxed mb-6"
            >
              {error || "This unsubscribe link may be expired or invalid."}
              {" "}Reply to any Salty Moo email and a real person will
              take you off the list by hand.
            </p>
            <Link
              to="/"
              className="btn-pasture inline-flex items-center justify-center"
            >
              Back to the barn doors
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
